import { useDeviceSetting } from "@-/dev.improve.haniverse.lib.device-settings";
import {
  buildUnitSequence,
  getPronunciationLessonGroupIndex,
} from "@-/dev.improve.haniverse.lib.lesson.get";
import {
  getSlideManifest,
  isBeforeAnchor,
  isEndAnchor,
  isStartAnchor,
  type SlideManifestEntry,
} from "@-/dev.improve.haniverse.assets.slides";
import {
  getSimplifiedLevels,
  getTraditionalLevels,
} from "@-/dev.improve.haniverse.assets.graphemes.levels";
import type { HanziScript } from "@-/dev.improve.haniverse.lib.actor.types";
import {
  buildCurriculumMaterialTabOptions,
  getCurriculumGradeParts,
  getCurriculumMaterialTabValue,
  type Level,
} from "@-/dev.improve.haniverse.lib.level";
import {
  availableHanziScript,
  useTraditionalCharactersEnabled,
} from "@-/dev.improve.haniverse.lib.posthog";
import { Platform } from "@-/com.npmjs.react-native";
import { useContextSelectors } from "@-/com.npmjs.use-context-selectors";

/** A level surface's cards: its characters, or the pronunciation level that follows them (`2-P1`). */
export type LevelSurfacePart = "character" | "pronunciation";

/** A level the list should bring to the top of its viewport, such as a pinyin chart sound's. */
export type LevelScrollRequest = { levelIndex: number; part: LevelSurfacePart };

/** Keys a level surface's presentation and slides: `levelIndex/part`. */
export function getLevelSurfaceKey(levelIndex: number, part: LevelSurfacePart): string {
  return `${levelIndex}/${part}`;
}

/** A card's slide lessons, in sequence order: those that lead into it, and those that follow it. */
export interface LevelCardSlides {
  after: readonly SlideManifestEntry[];
  before: readonly SlideManifestEntry[];
}

/** A level surface's slide lessons, under the card each one plays next to. */
export type LevelSurfaceSlides = ReadonlyMap<string, LevelCardSlides>;

/**
 * Each level surface's slide lessons, keyed by `getLevelSurfaceKey`, grouped under the card each one
 * plays next to in the lesson sequence: a `before` (or level `start`) slide under the card it leads
 * into (as one of its `before` slides), any other slide under the card it follows (`after`).
 * Surfaces without slides are absent.
 */
export function buildLevelSurfaceSlides(
  levels: readonly Level[],
  slideManifest: readonly SlideManifestEntry[] = getSlideManifest(),
): ReadonlyMap<string, LevelSurfaceSlides> {
  const grouped = new Map<string, Map<string, LevelCardSlides>>();
  const byIndex = new Map(levels.map((level) => [level.index, level]));
  const slidesByKey = new Map(slideManifest.map((slide) => [slide.key, slide]));
  // Each surface's most recently played card, and the slides still waiting for its next one.
  const lastCards = new Map<string, string>();
  const waitingSlides = new Map<string, SlideManifestEntry[]>();
  const attach = (
    key: string,
    character: string,
    side: keyof LevelCardSlides,
    slides: readonly SlideManifestEntry[],
  ) => {
    let surface = grouped.get(key);
    if (surface === undefined) {
      surface = new Map();
      grouped.set(key, surface);
    }
    const card = surface.get(character) ?? { after: [], before: [] };
    surface.set(character, { ...card, [side]: [...card[side], ...slides] });
  };
  for (const entry of buildUnitSequence(levels, slideManifest)) {
    if (entry.type === "morpheme") {
      // Primitive, meaning, and combined lessons all belong to the character surface.
      const part = entry.part === "pronunciation" ? "pronunciation" : "character";
      const key = getLevelSurfaceKey(entry.level.index, part);
      lastCards.set(key, entry.character);
      const waiting = waitingSlides.get(key);
      if (waiting !== undefined) {
        attach(key, entry.character, "before", waiting);
        waitingSlides.delete(key);
      }
      continue;
    }
    const levelIndex = Math.max(0, entry.sortKey[0] - 1);
    const level = byIndex.get(levelIndex);
    if (level === undefined) {
      continue;
    }
    const slide = slidesByKey.get(entry.key);
    if (slide === undefined) {
      continue;
    }
    let part: LevelSurfacePart =
      entry.lessonGroupIndex === getPronunciationLessonGroupIndex(level)
        ? "pronunciation"
        : "character";
    // A level boundary names its surface directly, even when the level has no lesson groups.
    if (isStartAnchor(slide.anchor)) {
      part = slide.anchor.start.part ?? "character";
    } else if (isEndAnchor(slide.anchor)) {
      part =
        slide.anchor.end.part ??
        ((level.pronunciationCharacters?.length ?? 0) > 0 ? "pronunciation" : "character");
    }
    const key = getLevelSurfaceKey(levelIndex, part);
    const lastCard = lastCards.get(key);
    if (lastCard === undefined || isBeforeAnchor(slide.anchor) || isStartAnchor(slide.anchor)) {
      waitingSlides.set(key, [...(waitingSlides.get(key) ?? []), slide]);
    } else {
      attach(key, lastCard, "after", [slide]);
    }
  }
  // A slide that no later card of its surface picks up stays with the surface's last card, after it.
  for (const [key, slides] of waitingSlides) {
    const lastCard = lastCards.get(key);
    if (lastCard !== undefined) {
      attach(key, lastCard, "after", slides);
    }
  }
  return grouped;
}

/** No slide lessons: they are authored against the user's own curriculum's lesson sequence. */
const NO_LEVEL_SURFACE_SLIDES: ReadonlyMap<string, LevelSurfaceSlides> = new Map();

import { useActorUser } from "@-/dev.improve.haniverse.lib.user";
import { NavContext } from "@-/dev.improve.haniverse.routes.nav._context";
import type { LevelsTopbarViewMode } from "@-/dev.improve.haniverse.routes.nav.app.browse.tabs.levels._components.toolbar";
import { useGetGraphemeGlyph } from "@-/dev.improve.haniverse.routes.nav.app._lib.morpheme.grapheme.glyph/client";
import { useEffect, useMemo, useState } from "react";
import { subscribeBrowseReset } from "@-/dev.improve.haniverse.routes.nav._lib.primary-navigation-strip";

/**
 * Shared state for the levels layout and its sub-routes. The completion filter is the active
 * sub-route (`all` / `complete` / `incomplete`), so it no longer lives here; instead this holds the
 * cross-route UI concerns: the grade, the view-mode toggle (glyph-tiles vs detail-tiles) that
 * must persist while switching filters, and the current-level scroll coordination that lets the
 * layout's floating toolbar drive whichever sub-route list is currently mounted.
 *
 * Study data (which characters are added, so which levels are complete) is NOT here: each leaf that
 * needs it subscribes to `useBrowseLevelsStudyGraphemesQuery()` itself.
 */
export function useBrowseLevelsLayoutContextValue() {
  const [selectedGrade, setSelectedGrade] = useState(0);
  const { hanziScript } = useActorUser();
  const traditionalCharactersEnabled = useTraditionalCharactersEnabled();
  // The curriculum the learner studies. The other one can be browsed from the toolbar's script
  // tabs, but not selected yet: its levels are a read-only preview under a "coming soon" banner.
  const studiedHanziScript = availableHanziScript(hanziScript, traditionalCharactersEnabled);
  const [browsedHanziScript, setBrowsedHanziScriptState] =
    useState<HanziScript>(studiedHanziScript);
  const isBrowsingStudiedCurriculum = browsedHanziScript === studiedHanziScript;
  const setBrowsedHanziScript = (script: HanziScript) => {
    if (script === browsedHanziScript) {
      return;
    }
    // HSK and TBCL grades are different tabs; open the other curriculum at its first one.
    setSelectedGrade(1);
    setBrowsedHanziScriptState(script);
  };
  const characterLevels: Level[] =
    browsedHanziScript === "traditional" ? getTraditionalLevels() : getSimplifiedLevels();
  const [showSlideLessons, setShowSlideLessons] = useDeviceSetting("showSlideLessons");
  // Owned above the recycling list, and scoped to the curriculum so matching level indices
  // in Simplified and Traditional keep their own expanded state.
  const [levelDetailsExpandedBySurface, setLevelDetailsExpandedBySurface] = useState<
    ReadonlyMap<string, boolean>
  >(new Map());
  const setLevelDetailsExpanded = (key: string, expanded: boolean) => {
    setLevelDetailsExpandedBySurface((previous) =>
      new Map(previous).set(`${browsedHanziScript}/${key}`, expanded),
    );
  };
  const slidesBySurface = useMemo(
    () =>
      isBrowsingStudiedCurriculum
        ? buildLevelSurfaceSlides(characterLevels)
        : NO_LEVEL_SURFACE_SLIDES,
    [characterLevels, isBrowsingStudiedCurriculum],
  );
  const [searchQuery, setSearchQuery] = useState("");
  const [viewMode, setViewModeState] = useState<LevelsTopbarViewMode>(
    Platform.OS === "web" ? "detail-tiles" : "glyph-tiles",
  );
  const setViewMode = (mode: LevelsTopbarViewMode) => {
    setLevelDetailsExpandedBySurface(new Map());
    setViewModeState(mode);
  };
  // Retain a focused Browse re-tap through completion-route and grade navigation.
  const [shouldScrollToCurrentLevel, setShouldScrollToCurrentLevel] = useState(false);
  // Likewise a level asked for from the pack sheet, until a list that shows it scrolls there.
  const [levelScrollRequest, setLevelScrollRequest] = useState<LevelScrollRequest | null>(null);
  useEffect(() => subscribeBrowseReset(() => setShouldScrollToCurrentLevel(true)), []);
  const [hanziScriptOverride] = useContextSelectors(NavContext, (c) => c.hanziScriptOverride);
  const effectiveHanziScript = availableHanziScript(
    hanziScriptOverride ?? hanziScript,
    traditionalCharactersEnabled,
  );

  // Word levels sit between character levels, so they share their character level's material tab.
  const curriculumDataset: "hsk" | "tbcl" = browsedHanziScript === "traditional" ? "tbcl" : "hsk";
  const gradeParts = useMemo(
    () => getCurriculumGradeParts(characterLevels, { dataset: curriculumDataset }),
    [characterLevels, curriculumDataset],
  );
  const gradeOptions = useMemo(
    () => buildCurriculumMaterialTabOptions(characterLevels, { dataset: curriculumDataset }),
    [characterLevels, curriculumDataset],
  );
  const curriculumGrade = gradeOptions.some((option) => option.value === selectedGrade)
    ? selectedGrade
    : (gradeOptions[0]?.value ?? 1);
  // Every pack of the studied curriculum hangs a sheet (its description, then its pinyin charts or
  // progress) from the pack tabs; a preview of a curriculum that is coming soon has none.
  const hasPackSheet = browsedHanziScript !== "traditional" && isBrowsingStudiedCurriculum;
  const requestLevelScroll = (request: LevelScrollRequest) => {
    const level = characterLevels[request.levelIndex];
    if (level === undefined) {
      return;
    }
    // A level in another pack opens that pack first; its list then scrolls to the level.
    setSelectedGrade(
      getCurriculumMaterialTabValue(level, { dataset: curriculumDataset, gradeParts }),
    );
    setLevelScrollRequest(request);
  };

  const getGraphemeGlyph = useGetGraphemeGlyph();
  const getToggledGraphemeGlyph = (grapheme: string) =>
    getGraphemeGlyph(grapheme, { hanziScript: effectiveHanziScript, uncolored: true });

  return {
    browsedHanziScript,
    characterLevels,
    curriculumDataset,
    curriculumGrade,
    getToggledGraphemeGlyph,
    gradeOptions,
    gradeParts,
    hasPackSheet,
    isBrowsingStudiedCurriculum,
    levelDetailsExpandedBySurface,
    levelScrollRequest,
    requestLevelScroll,
    searchQuery,
    setBrowsedHanziScript,
    setCurriculumGrade: setSelectedGrade,
    setLevelDetailsExpanded,
    setLevelScrollRequest,
    setSearchQuery,
    setShouldScrollToCurrentLevel,
    setShowSlideLessons,
    setViewMode,
    shouldScrollToCurrentLevel,
    showSlideLessons,
    slidesBySurface,
    viewMode,
  };
}

export type BrowseLevelsLayoutContextValue = ReturnType<typeof useBrowseLevelsLayoutContextValue>;
